import React from 'react';
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  SplitButton,
  type MenuButtonProps,
} from '@fluentui/react-components';
import {
  PersonCall24Regular,
  Building24Regular,
} from '@fluentui/react-icons';
import { ToolbarConfig, PrimaryActionButton, PageLayoutConfig } from '../types/toolbar';
import { BOOQABLE_ADMIN_URL } from './booqable';
import { isLocalhost } from './appEnv';
import { RowMenuItem } from '../components/DataTable';
import {
  AddCircle16Regular,
  Box24Regular,
  BinFull24Regular,
  Filter24Regular,
  ChartMultiple24Regular,
  Add24Regular,
  ArrowDownload24Regular,
  ArrowUpload24Regular,
  ArrowSync24Regular,
  Copy24Regular,
  CalendarLtr24Regular,
  Print24Regular,
  Grid20Regular,
  TableSimple20Regular,
  Mail24Regular,
  Edit24Regular,
  Archive20Regular,
  PeopleTeam24Regular,
  History24Regular,
  Video24Regular,
  VehicleCar24Regular,
  Chat24Regular,
  Share24Regular,
  BuildingMultiple24Regular,
  Map24Regular,
  Eye20Regular,
  DocumentEdit20Regular,
  Print20Regular,
  Delete20Regular,
  DismissCircle20Regular,
  Open20Regular,
  Send24Regular,
  Navigation24Regular,
  Location24Regular,
  List24Regular,
  LockClosed20Regular,
  Play20Regular,
  CheckmarkCircle20Regular,
  ArrowUndo20Regular,
} from '@fluentui/react-icons';
import { useIsMobile } from '../hooks/useIsMobile';

interface ToolbarMap { [key: string]: ToolbarConfig; }
interface PrimaryActionMap { [key: string]: PrimaryActionButton; }
interface LayoutConfigMap { [key: string]: PageLayoutConfig; }
interface RowMenuConfigMap { [key: string]: RowMenuItem[]; }

// Split button for "Add Customer" — primary click defaults to Individual
// (the most common case); the dropdown lets staff pre-select Contact or
// Company so the drawer opens with the right kind already chosen. Each
// option dispatches the same `addNewCustomer` CustomEvent with the kind
// in `detail` so the Customers page handler can pass it to the drawer.
// "Import from CSV" fires a separate `importCustomers` event which the
// Customers page wires up to ImportCustomersModal.
export const AddCustomerSplitButton: React.FC = () => {
  const isMobile = useIsMobile();
  const dispatchAdd = (kind?: 'individual' | 'contact' | 'company') => {
    window.dispatchEvent(new CustomEvent('addNewCustomer', { detail: { kind } }));
  };
  const dispatchImport = () => {
    window.dispatchEvent(new CustomEvent('importCustomers'));
  };
  return (
    <Menu positioning="below-end">
      <MenuTrigger disableButtonEnhancement>
        {(triggerProps: MenuButtonProps) => (
          <SplitButton
            appearance="primary"
            icon={<Add24Regular />}
            menuButton={triggerProps}
            primaryActionButton={{ onClick: () => dispatchAdd(), 'aria-label': 'Add customer' }}
          >
            {isMobile ? undefined : 'Add Customer'}
          </SplitButton>
        )}
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem icon={<PersonCall24Regular />} onClick={() => dispatchAdd('contact')}>
            Add Contact
          </MenuItem>
          <MenuItem icon={<Building24Regular />} onClick={() => dispatchAdd('company')}>
            Add Company
          </MenuItem>
          {/* Import from CSV is incomplete — disabled until the flow is finished. */}
          <MenuItem icon={<ArrowUpload24Regular />} onClick={dispatchImport} disabled>
            Import from CSV
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};


export const toolbarConfig: ToolbarMap = {
  '/customers': [
    {
      label: '',
      appearance: 'primary',
      customComponent: AddCustomerSplitButton,
    },
    // HasOrders / HasPaymentMethod filters moved into the left filter
    // sidebar on the Customers page (see CustomerFiltersSidebar).
    // The Sync Booqable button lives in the Customers page's inline Header
    // customActions, not here — toolbarConfig is bypassed for that surface.
  ],
  '/orders': [
    {
      label: 'Add Order',
      icon: <Add24Regular />,
      appearance: 'primary',
      onClick: () => window.dispatchEvent(new CustomEvent('navigateToNewOrder')),
    },
    // Manual Booqable → Supabase reconcile lives on the developer's machine
    // only; webhooks handle prod/sandbox. See isLocalhost() in appEnv.ts.
    ...(isLocalhost() ? [{
      label: 'Sync Booqable',
      icon: <ArrowSync24Regular />,
      appearance: 'secondary' as const,
      onClick: () => window.dispatchEvent(new CustomEvent('syncBooqableOrders')),
    }] : []),
  ],
  '/product_groups': [
    {
      label: 'Sync Booqable',
      icon: <ArrowSync24Regular />,
      appearance: 'secondary',
      onClick: () => window.dispatchEvent(new CustomEvent('syncBooqableProducts')),
    },
    // Add Product deferred — re-enable when product creation flow is built.
    // {
    //   label: 'Add Product',
    //   icon: <Add24Regular />,
    //   appearance: 'primary',
    //   onClick: () => console.log('Add Product clicked'),
    // },
  ],
  '/tasks': [
    {
      label: 'Add Task',
      icon: <Add24Regular />,
      appearance: 'primary',
      onClick: () => window.dispatchEvent(new CustomEvent('addNewTask')),
    },
  ],
  '/fleet/active-reminders': [
    {
      label: 'RESET FILTERS',
      appearance: 'secondary',
      onClick: () => console.log('Reset filters clicked'),
    },
    {
      label: 'EXPORT',
      icon: <ArrowDownload24Regular />,
      appearance: 'secondary',
      onClick: () => console.log('Export clicked'),
    },
    {
      label: 'COPY REMINDERS',
      icon: <Copy24Regular />,
      appearance: 'secondary',
      onClick: () => console.log('Copy reminders clicked'),
    },
    {
      label: 'CREATE ONE-TIME SERVICE EVENT',
      appearance: 'secondary',
      onClick: () => window.dispatchEvent(new CustomEvent('triggerOneTimeService')),
    },
  ],
  '/maintenance': [

  ],
  '/customers/:customerId': [],
  // DocumentEdit renders its own toolbar inline via Header's `customActions`,
  // so the global route-toolbar slot is intentionally empty here.
  '/documents/:documentId': [],
  '/operations-map': [
    // No toolbar buttons - map view is always displayed
  ],
};

export const primaryActionConfig: PrimaryActionMap = {
  '/orders': {
    label: 'Add Order',
    icon: <AddCircle16Regular />,
    onClick: () => window.dispatchEvent(new CustomEvent('navigateToNewOrder')),
  },
  '/customers': {
    label: 'Add Customer',
    icon: <AddCircle16Regular />,
    onClick: () => window.dispatchEvent(new CustomEvent('addNewCustomer')),
  },
  // Add Product deferred — re-enable when product creation flow is built.
  // '/product_groups': {
  //   label: 'Add Product',
  //   icon: <AddCircle16Regular />,
  //   onClick: () => console.log('Create new product'),
  // },
  '/fleet/active-reminders': {
    label: 'ADD NEW REMINDER',
    icon: <Add24Regular />,
    onClick: () => console.log('Add new reminder clicked'),
  },
  '/maintenance': {
    label: 'ADD NEW REMINDER',
    icon: <Add24Regular />,
    onClick: () => window.dispatchEvent(new CustomEvent('triggerAddReminder')),
  },
  '/customers/:customerId': {
    label: 'Edit Customer',
    icon: <Edit24Regular />,
  },
};

// Helper function to match dynamic routes
const matchRoute = (pathname: string, pattern: string): boolean => {
  // Convert pattern to regex (e.g., /customers/:customerId -> /customers/[^/]+)
  const regexPattern = pattern.replace(/:[^/]+/g, '[^/]+');
  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(pathname);
};

// Helper function to find matching config
const findMatchingConfig = <T,>(pathname: string, configMap: { [key: string]: T }): T | null => {
  // First try exact match
  if (configMap[pathname]) {
    return configMap[pathname];
  }

  // Then try pattern matching for dynamic routes
  for (const pattern in configMap) {
    if (pattern.includes(':') && matchRoute(pathname, pattern)) {
      return configMap[pattern];
    }
  }

  return null;
};

// Page layout configuration
export const layoutConfig: LayoutConfigMap = {
  // Dashboard renders no header band of its own.
  '/': {
    showHamburger: true,
    showDetailsPane: false,
    suppressGlobalHeader: true,
  },
  // Messaging owns its full layout, including its own header.
  '/messaging': {
    showHamburger: true,
    showDetailsPane: false,
    suppressGlobalHeader: true,
  },
  '/customers': {
    // LeftRail toggle hidden — the page-local filter-panel toggle
    // (passed via Header.leftActions) is the only chrome control this
    // surface needs in the Header.
    showHamburger: false,
    showDetailsPane: false,
    embedToolbarInPage: true,
    suppressGlobalHeader: true,
  },
  '/customers/:customerId': {
    // Profile surface doesn't need its own LeftRail toggle — the
    // breadcrumb provides "Customers" navigation back to the list.
    showHamburger: false,
    showDetailsPane: false,
    suppressGlobalHeader: true,
  },
  '/orders': {
    // LeftRail toggle hidden — the page-local filter-panel toggle (passed
    // via Header.leftActions) is the only chrome control this surface needs.
    // Mirrors the /customers configuration.
    showHamburger: false,
    showDetailsPane: false,
    suppressGlobalHeader: true,
  },
  '/tasks': {
    showHamburger: false,
    showDetailsPane: false,
  },
  '/operations-map': {
    showHamburger: false,
    showDetailsPane: true,
  },
  '/booking': {
    showHamburger: true,
    showDetailsPane: false,
  },
  '/documents': {
    showHamburger: false,
    showDetailsPane: true,
  },
  '/documents/:documentId': {
    // DocumentEdit renders its own breadcrumb + toolbar inline.
    showHamburger: false,
    showDetailsPane: false,
    suppressGlobalHeader: true,
  },
  '/product_groups': {
    showHamburger: false,
    showDetailsPane: false,
    suppressGlobalHeader: true,
  },
  '/loadboard': {
    showHamburger: true,
    showDetailsPane: false,
    suppressGlobalHeader: true,
  },
  '/orders/new': {
    // Page renders its own inline Header + right sidebar inside the
    // workspace card, so the global details-pane toggle is suppressed
    // here to avoid a duplicate panel-right button on the left.
    showHamburger: false,
    showDetailsPane: false,
    suppressGlobalHeader: true,
  },
  '/orders/:orderId': {
    showHamburger: false,
    showDetailsPane: false,
    suppressGlobalHeader: true,
  },
  '/projects': {
    // No left sidebar on this surface — hide the LeftRail toggle so the
    // Header doesn't carry a control that opens nothing.
    showHamburger: false,
    showDetailsPane: false,
    suppressGlobalHeader: true,
  },
  '/projects/:projectId': {
    // Detail surface has no sidebar either — drop the LeftRail toggle.
    showHamburger: false,
    showDetailsPane: false,
    suppressGlobalHeader: true,
  },
  // Renders its own inline Header in a workspace card (unified-surface).
  '/users': {
    showHamburger: true,
    showDetailsPane: false,
    suppressGlobalHeader: true,
  },
  '/settings/sale-items': {
    showHamburger: true,
    showDetailsPane: false,
    suppressGlobalHeader: true,
  },
  // Add more routes as needed. A page that renders its own inline header
  // (workspace-card pattern) just needs `suppressGlobalHeader: true` here.
};

export const getToolbarForRoute = (pathname: string): ToolbarConfig | null => {
  return findMatchingConfig(pathname, toolbarConfig);
};

export const getLayoutConfigForRoute = (pathname: string): PageLayoutConfig => {
  const config = findMatchingConfig(pathname, layoutConfig);
  if (config) return config;
  // Every /settings/* page renders its own inline Header inside a workspace
  // card (SettingsLayout / SaleItems), so suppress the global header band for
  // the whole subtree without enumerating each route.
  if (pathname === '/settings' || pathname.startsWith('/settings/')) {
    return { showHamburger: false, showDetailsPane: false, suppressGlobalHeader: true };
  }
  // Return defaults if no config found
  return { showHamburger: true, showDetailsPane: false };
};

// Row menu configuration - defines contextual menu items for table rows
export const rowMenuConfig: RowMenuConfigMap = {
  // Every lifecycle action is always listed; illegal-for-this-state actions
  // render disabled (greyed, with a `disabledReason` tooltip) rather than
  // disappearing, so staff can see the full action set at a glance. Legality
  // mirrors the backend transition guards (booqable_*_order in app.py) and the
  // OrderDetails primary-action gating. `status` is the Booqable lifecycle:
  // draft → reserved → started → stopped("Completed") → archived, plus the
  // terminal `canceled`.
  '/orders': (() => {
    const status = (order: any): string => (order?.booqableStatus || '').toLowerCase();
    const isAny = (order: any, ...states: string[]) => states.includes(status(order));
    return [
      {
        key: 'share',
        label: 'Share',
        icon: <Share24Regular />,
        onClick: (order: any) => {
          window.dispatchEvent(new CustomEvent('shareOrder', { detail: order }));
        },
      },
      {
        key: 'edit',
        label: 'Edit Order',
        icon: <DocumentEdit20Regular />,
        onClick: (order: any) => {
          window.location.href = `/orders/${order.id}`;
        },
      },
      // ── Lifecycle (forward + back) ──
      {
        key: 'reserve',
        label: 'Reserve',
        icon: <LockClosed20Regular />,
        dividerBefore: true,
        // Reserve has a prohibited-items acknowledgment gate (and a rental-period
        // requirement) that lives on the detail page — deep-link there rather
        // than reserving blind from the list.
        onClick: (order: any) => {
          window.location.href = `/orders/${order.id}`;
        },
        disabled: (order: any) => !isAny(order, 'new', 'draft'),
        disabledReason: () => 'Only draft orders can be reserved.',
      },
      {
        key: 'start',
        label: 'Start',
        icon: <Play20Regular />,
        // Start needs the "which items are going out" picker (partial vs full
        // start) on the detail page — deep-link there.
        onClick: (order: any) => {
          window.location.href = `/orders/${order.id}`;
        },
        disabled: (order: any) => !isAny(order, 'reserved'),
        disabledReason: () => 'Only reserved orders can be started.',
      },
      {
        key: 'complete',
        label: 'Complete',
        icon: <CheckmarkCircle20Regular />,
        onClick: (order: any) => {
          window.dispatchEvent(new CustomEvent('completeOrder', { detail: order }));
        },
        disabled: (order: any) => !isAny(order, 'started'),
        disabledReason: () => 'Only started orders can be completed.',
      },
      {
        key: 'archive',
        label: 'Archive Order',
        icon: <Archive20Regular />,
        onClick: (order: any) => {
          window.dispatchEvent(new CustomEvent('archiveOrder', { detail: order }));
        },
        disabled: (order: any) => !isAny(order, 'stopped', 'canceled'),
        disabledReason: () => 'Only completed or canceled orders can be archived.',
      },
      {
        key: 'unarchive',
        label: 'Un-archive',
        icon: <ArrowUndo20Regular />,
        onClick: (order: any) => {
          window.dispatchEvent(new CustomEvent('unarchiveOrder', { detail: order }));
        },
        disabled: (order: any) => !isAny(order, 'archived'),
        disabledReason: () => 'Only archived orders can be un-archived.',
      },
      // ── Terminal ──
      {
        key: 'cancel',
        label: 'Cancel Order',
        icon: <DismissCircle20Regular />,
        dividerBefore: true,
        // Booqable only allows cancel from new/draft/reserved.
        onClick: (order: any) => {
          window.dispatchEvent(new CustomEvent('cancelOrder', { detail: order }));
        },
        disabled: (order: any) => !isAny(order, 'new', 'draft', 'reserved'),
        disabledReason: () => 'Only new, draft, or reserved orders can be canceled.',
      },
    ];
  })(),
  '/customers': [
    {
      key: 'add-order',
      label: 'Add order',
      icon: <Add24Regular />,
      // Contacts can't own orders independently (they belong to a company),
      // so the action only makes sense for individuals and companies.
      hidden: (customer: any) => customer?.customerKind === 'contact',
      // Routes to the new-order flow with this customer pre-selected. The
      // Customers page handler navigates via react-router (SPA) rather than a
      // full reload — same destination as the profile page's "Add order"
      // button (/orders/new?customer=<id>, consumed by OrderDetails).
      onClick: (customer: any) => {
        window.dispatchEvent(new CustomEvent('addOrderForCustomer', { detail: customer.id }));
      },
    },
    {
      key: 'share',
      label: 'Share',
      icon: <Share24Regular />,
      onClick: (customer: any) => {
        window.dispatchEvent(new CustomEvent('shareCustomer', { detail: customer }));
      },
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <Delete20Regular />,
      onClick: (customer: any) => {
        window.dispatchEvent(new CustomEvent('deleteCustomer', { detail: customer.id }));
      },
    },
  ],
  '/documents': [
    {
      key: 'share',
      label: 'Share',
      icon: <Share24Regular />,
      onClick: (document: any) => {
        window.dispatchEvent(new CustomEvent('shareDocument', { detail: document }));
      },
    },
    {
      key: 'edit',
      label: 'Edit Document',
      icon: <DocumentEdit20Regular />,
      onClick: (document: any) => {
        window.location.href = `/documents/${document.id}`;
      },
    },
    {
      key: 'print',
      label: 'Print Document',
      icon: <Print20Regular />,
      onClick: (document: any) => {
        window.dispatchEvent(new CustomEvent('printDocument', { detail: document }));
      },
    },
  ],
  '/product_groups': [
    {
      key: 'edit',
      label: 'Edit',
      icon: <Edit24Regular />,
      onClick: (product: any) => {
        window.dispatchEvent(new CustomEvent('editProduct', { detail: product.id }));
      },
    },
  ],
  '/projects': [
    {
      key: 'delete',
      label: 'Delete',
      icon: <Delete20Regular />,
      onClick: (project: any) => {
        window.dispatchEvent(new CustomEvent('deleteProject', { detail: project }));
      },
    },
  ],
  '/tasks': [
    {
      key: 'share',
      label: 'Share',
      icon: <Share24Regular />,
      onClick: (task: any) => {
        window.dispatchEvent(new CustomEvent('shareTask', { detail: task }));
      },
    },
    {
      key: 'edit',
      label: 'Edit',
      icon: <Edit24Regular />,
      onClick: (task: any) => {
        window.dispatchEvent(new CustomEvent('editTask', { detail: task }));
      },
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <Delete20Regular />,
      onClick: (task: any) => {
        window.dispatchEvent(new CustomEvent('deleteTask', { detail: task }));
      },
    },
  ],
  // Add more routes as needed
};

export const getRowMenuForRoute = (pathname: string): RowMenuItem[] | null => {
  return findMatchingConfig(pathname, rowMenuConfig);
};

/*export const getPrimaryActionForRoute = (pathname: string): PrimaryActionButton | null => {
  return findMatchingConfig(pathname, primaryActionConfig);
};
*/